// Shared table configuration for database tables
export interface TableConfig {
  columns?: string;
  primaryKey: string;
}

export type TableConfigs = Record<string, TableConfig>;

export const TABLE_CONFIG: TableConfigs = {
  unidades: {
    columns: "id, created_at, updated_at, nombre, descripcion, hectareas_total, empresa_id, is_deleted",
    primaryKey: "id"
  },
  ubicaciones: {
    columns: "id, created_at, updated_at, nombre, descripcion, unidad_id, hectareas, tipo, empresa_id, is_deleted",
    primaryKey: "id"
  },
  animales: {
    columns: "*",
    primaryKey: "caravana"
  },
  animales_historico: {
    columns: "*",
    primaryKey: "id"
  },
  empresas: {
    columns: "id, created_at, updated_at, nombre, descripcion, renspa, peso_objetivo_venta, peso_objetivo_novillo",
    primaryKey: "id"
  },
  profiles: {
    columns: "empresa_activa_id, user_id",
    primaryKey: "empresa_activa_id"
  },
  eventos: {
    columns: "id, title, description, date, time, category, completed, user_id, empresa_id, is_public, created_at, updated_at, is_deleted",
    primaryKey: "id"
  },
  notas: {
    columns: "id, title, content, category, user_id, empresa_id, is_public, created_at, updated_at, is_deleted, created_by, previous_content, previous_updated_at, tipo, datos, fecha_evento, realizado_por_texto, realizado_por_user_id, realizado_por_persona_id, realizado_por_nombre, solo_finanzas",
    primaryKey: "id"
  },
  nota_anclas: {
    columns: "id, empresa_id, nota_id, tipo_ancla, ancla_id, periodo_desde, periodo_hasta, caravanas, created_by, created_at, updated_at, is_deleted",
    primaryKey: "id"
  },
  personas_campo: {
    columns: "id, empresa_id, nombre, rol, created_by, created_at, updated_at, is_deleted",
    primaryKey: "id"
  },
  recordatorios: {
    columns: "id, title, description, date, time, priority, completed, user_id, empresa_id, is_public, created_at, updated_at, is_deleted",
    primaryKey: "id"
  },
  lluvias: {
    columns: "id, fecha, milimetros, ubicacion_id, latitud, longitud, observaciones, user_id, empresa_id, created_at, updated_at, is_deleted",
    primaryKey: "id"
  },
  elementos_mapa: {
    columns: "id, tipo, nombre, geometria, color, hectareas, notas, bioma_default, ubicacion_id, inter_cuadro, cuadros_conectados, diametro_mm, ambiente_clave, lote_estado, riego, user_id, empresa_id, created_at, updated_at, is_deleted",
    primaryKey: "id"
  },
  puntos_control: {
    columns: "id, empresa_id, nombre, lng, lat, ambiente_clave, origen_ambiente, notas, creado_por, created_at, updated_at, is_deleted",
    primaryKey: "id"
  },
  puntos_control_eventos: {
    columns: "id, punto_id, empresa_id, fecha_observacion, condicion_pastura, densidad, plantas_observadas, ambiente_clave, masa_seca_kg_ha, metodo_masa_seca, tipo_toma, altura_vegetacion, altura_vegetacion_cm, tipo_pastura, calculo_pastura, corte_ms, plantas_checklist, cobertura, analisis_foto, analisis_estado, cobertura_forrajera_pct, notas, fotos, creado_por, created_at, updated_at, is_deleted",
    primaryKey: "id"
  },
  siembras_lote: {
    columns: "id, elemento_id, empresa_id, cultivo, cultivo_modo, ciclo, fecha_inicio, fecha_inicio_aproximada, fecha_fin, motivo_fin, origen, estado, fin_estado, fin_propuesto_fecha, resumen, user_id, created_at, updated_at, is_deleted",
    primaryKey: "id"
  },
  sigsa_declaraciones: {
    columns: "id, session_code, caravanas, cantidad, fecha_declaracion, created_by, empresa_id, created_at, updated_at, is_deleted",
    primaryKey: "id"
  },
  sync_acciones: {
    columns: "id, empresa_id, user_id, tipo, payload, estado, error, resultado, created_at, updated_at, ejecutada_at",
    primaryKey: "id"
  },
  gastos: {
    columns: "id, fecha_evento, concepto, descripcion, monto, ubicacion_id, categoria, empresa_id, user_id, created_at, updated_at, is_deleted",
    primaryKey: "id"
  },
  ingresos: {
    columns: "id, fecha_evento, concepto, descripcion, monto, categoria, empresa_id, user_id, created_at, updated_at, is_deleted",
    primaryKey: "id"
  },
  stocks: {
    columns: "id, nombre, categoria, concepto_id, empresa_id, updated_at, fecha_evento, unidad_medida, cantidad, fecha_creacion",
    primaryKey: "id"
  },
  stocks_historico: {
    columns: "id, stock_id, fecha_evento, tipo_operacion, cantidad_operacion, cantidad_resultante, precio_total, nombre, categoria, concepto_id, unidad_medida, ubicacion_id, empresa_id, user_id, created_at, updated_at, is_deleted",
    primaryKey: "id"
  },
  categorias_cache: {
    columns: "id, tipo, nombre, uso_count",
    primaryKey: "id"
  },
  operacion_compra_venta_animales: {
    columns: "*",
    primaryKey: "uuid"
  },
  // Taxonomía de conceptos (spec 2026-08-07).
  //
  // conceptos_ocultos/conceptos_confirmados NO tienen columna `id`: su PK real
  // es compuesta (empresa_id, concepto_id). El sync envía los DELETE como
  // `.in('concepto_id', ids)`; el RLS de Supabase acota el borrado a la empresa
  // del usuario (la policy USING filtra por empresa_id), y como un mismo
  // concepto solo puede estar oculto una vez por empresa, el resultado es el
  // borrado exacto de la fila propia.
  conceptos: {
    columns: "*",
    primaryKey: "id"
  },
  categorias: {
    columns: "*",
    primaryKey: "id"
  },
  conceptos_ocultos: {
    columns: "empresa_id, concepto_id, created_at",
    primaryKey: "concepto_id"
  },
  conceptos_confirmados: {
    columns: "empresa_id, concepto_id, created_at",
    primaryKey: "concepto_id"
  }
};

export interface PendingChange {
  id?: number;
  table: string;
  action: 'INSERT' | 'UPDATE' | 'DELETE' | 'BULK_INSERT' | 'BULK_DELETE' | 'BULK_UPDATE';
  record: Record<string, any> | string | Record<string, any>[] | string[];
  retryCount?: number;
  lastError?: string;
}
